import React, { useState } from 'react';
import { Plus, MessageSquare, ChevronLeft, ChevronRight, Trash2, Pencil, MoreHorizontal, BarChart3, LayoutGrid, Sparkles, Code2, GitBranch } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import SettingsModal from './SettingsModal';
import AccountMenu from './AccountMenu';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { humanizeToken } from '../lib/colorUtils';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './ui/alert-dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator
} from './ui/select';

const Sidebar = ({
  conversations,
  activeConversation,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
  brandMeta,
  brandProfileSpec,
  brandApplicationSlice,
  brandContextPacks,
  brandVisualTheme,
  sessionUser,
  onLogin,
  onLogout,
  authStatus,
  accountLabel,
  portalBaseUrl,
  brands,
  activeBrandId,
  onBrandChange,
  availableEnvironments,
  activeEnvironment,
  onEnvironmentChange,
  onOpenResearch,
  onOpenSiteAgent,
  onUpdateUser,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isBrandOpen, setIsBrandOpen] = useState(true);
  const [isToolsOpen, setIsToolsOpen] = useState(true);
  const [isChatsOpen, setIsChatsOpen] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const conversationList = Array.isArray(conversations) ? conversations : [];
  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const isDark = resolvedTheme === 'dark';
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const brandOptions = Array.isArray(brands) ? brands : [];
  const environmentOptions = Array.isArray(availableEnvironments) ? availableEnvironments : [];
  const selectedBrandId = activeBrandId ?? brandMeta?.id ?? (brandOptions[0]?.id ?? '');
  const selectedEnvironment = activeEnvironment ?? 'production';

  if (process.env.NODE_ENV !== 'production' && !Array.isArray(conversations)) {
    console.warn('[BrandOnUI] Sidebar received non-array conversations payload', conversations);
  }

  const parseDate = (value) => {
    if (!value) return null;
    const raw = value instanceof Date ? value : new Date(value);
    if (isNaN(raw.getTime())) return null;
    return raw;
  };

  const formatDate = (input) => {
    const date = parseDate(input);
    if (!date) return '';
    
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const safePortalUrl = (portalBaseUrl ?? 'https://app.noderiver.ai').replace(/\/$/, '');
  const brandOsLink = `${safePortalUrl}/brand-os`;
  const workspacesLink = `${safePortalUrl}/brand-os?view=workspaces`;

  const containerClasses = cn(
    'h-full flex flex-col transition-all duration-300 overflow-hidden',
    collapsed ? 'w-16' : 'w-72'
  );

  const containerStyle = isBrandTheme
    ? { background: brandVisualTheme?.gradients?.hero, color: '#ffffff' }
    : { backgroundColor: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#ffffff' : '#0f172a' };

  const borderColor = isBrandTheme ? 'border-white/10' : (isDark ? 'border-gray-800' : 'border-slate-200');
  const brandCardClasses = isBrandTheme 
    ? 'bg-white/5 border border-white/10 rounded-2xl' 
    : (isDark ? 'bg-gray-800/50 rounded-2xl border border-gray-800/60' : 'bg-slate-50 rounded-2xl border border-slate-200');
  const badgeClasses = isBrandTheme 
    ? 'border-white/20 text-white' 
    : (isDark ? 'border-green-500 text-green-400' : 'border-green-600 text-green-700 bg-green-50');
  const conversationActiveClasses = isBrandTheme
    ? 'bg-white/10 text-white border border-white/10'
    : (isDark ? 'bg-gray-800 text-white' : 'bg-slate-100 text-slate-900');
  const conversationInactiveClasses = isBrandTheme
    ? 'text-white/70 hover:bg-white/5 hover:text-white'
    : (isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900');
  const themeLogo = brandVisualTheme?.logosByMode?.[resolvedTheme] ?? brandVisualTheme?.logo;
  const logoBadge = themeLogo?.badge ?? null;
  const buttonGradient =
    themeMode?.ctaGradient ?? brandVisualTheme?.gradients?.cta ?? 'linear-gradient(135deg, #1E8997, #0e273a)';

  const applicationSlice = brandApplicationSlice ?? null;
  const brandDisplayName =
    brandVisualTheme?.brandName ??
    applicationSlice?.brand ??
    brandProfileSpec?.brand?.name ??
    brandMeta?.name ??
    "BrandOn";
  const brandDifferentiator =
    brandVisualTheme?.tagline ??
    humanizeToken(applicationSlice?.voice?.differentiator) ??
    brandProfileSpec?.brand?.industry ??
    brandMeta?.industry ??
    "Industry: n/a";
  const voiceStyle = Array.isArray(applicationSlice?.voice?.voice_style)
    ? applicationSlice.voice.voice_style.join(", ")
    : null;

  const primaryCapsule = Array.isArray(brandContextPacks?.capsules)
    ? brandContextPacks.capsules[0]
    : null;

  const openRenameDialog = (conversation) => {
    setRenameTarget(conversation);
    setRenameValue(conversation?.title ?? '');
    setRenameOpen(true);
  };

  const closeRenameDialog = () => {
    setRenameOpen(false);
    setRenameTarget(null);
    setRenameValue('');
    setIsRenaming(false);
  };

  const handleRenameSubmit = async () => {
    if (!onRenameConversation || !renameTarget) return;
    const normalized = renameValue.trim();
    if (!normalized) {
      setRenameValue('');
      return;
    }
    setIsRenaming(true);
    try {
      await Promise.resolve(onRenameConversation(renameTarget.id, normalized));
      closeRenameDialog();
    } catch (err) {
      console.error('Failed to rename conversation', err);
      setIsRenaming(false);
    }
  };

  const openDeleteDialog = (conversation) => {
    setDeleteTarget(conversation);
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setDeleteTarget(null);
    setIsDeleting(false);
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteConversation || !deleteTarget) return;
    setIsDeleting(true);
    try {
      await Promise.resolve(onDeleteConversation(deleteTarget.id));
      closeDeleteDialog();
    } catch (err) {
      console.error('Failed to delete conversation', err);
      setIsDeleting(false);
    }
  };

  return (
    <>
    <div className={containerClasses} style={containerStyle}>
      {/* Header */}
      <div className={cn('px-3 py-3 border-b', borderColor)}>
        <div className="flex items-center justify-between mb-0">
          {!collapsed && (
            <div className="flex items-center space-x-3 px-1">
              {themeLogo?.uri ? (
                <div
                  className="inline-flex items-center justify-center"
                  style={
                    logoBadge
                      ? {
                          backgroundColor: logoBadge.background ?? 'rgba(255,255,255,0.95)',
                          border: logoBadge.border ? `1px solid ${logoBadge.border}` : 'none',
                          opacity: typeof logoBadge.opacity === 'number' ? logoBadge.opacity : 1,
                          padding:
                            typeof logoBadge.padding_px === 'number'
                              ? `${logoBadge.padding_px}px`
                              : '6px 14px',
                          borderRadius:
                            logoBadge.shape === 'pill'
                              ? '9999px'
                              : logoBadge.shape === 'circle'
                                ? '9999px'
                                : logoBadge.shape === 'rounded'
                                  ? '12px'
                                  : logoBadge.shape === 'none'
                                    ? '0px'
                                    : '4px',
                          backdropFilter: 'blur(6px)',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                        }
                      : undefined
                  }
                >
                  <img
                    src={themeLogo.uri}
                    alt={themeLogo.alt ?? 'Brand logo'}
                    className={logoBadge ? 'h-6 w-auto' : 'h-8 w-auto'}
                  />
                </div>
              ) : null}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'p-1 h-8 w-8 rounded-lg',
              isBrandTheme
                ? 'text-white/70 hover:bg-white/10'
                : (isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100')
            )}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
        
        {!collapsed && (
          <div className="mt-3 px-1">
            <Button
              onClick={onNewConversation}
              className="w-full flex items-center justify-start gap-3 rounded-lg text-sm font-medium h-10 px-3 transition-all shadow-sm border border-white/10 text-white hover:opacity-90 shadow-md"
              style={{ background: buttonGradient }}
            >
              <Plus className="w-4 h-4" />
              New Chat
            </Button>
          </div>
        )}
      </div>

      {/* Brand Info */}
      {!collapsed && (brandProfileSpec || applicationSlice || brandMeta) && (
        <Collapsible
          open={isBrandOpen}
          onOpenChange={setIsBrandOpen}
          className={cn('px-3 py-2 mx-2 my-1 transition-all hover:bg-black/5 dark:hover:bg-white/5', brandCardClasses)}
        >
          <CollapsibleTrigger className="flex items-center justify-between w-full group cursor-pointer">
             <div className="flex items-center gap-2">
                <ChevronRight className={cn("w-3 h-3 transition-transform duration-200 opacity-50", isBrandOpen && "rotate-90")} />
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-50">Active Brand</span>
             </div>
             <Badge variant="secondary" className={cn('text-[10px] h-5 px-1.5 font-normal', badgeClasses)}>
               {brandMeta?.status ?? primaryCapsule?.channel ?? "Active"}
             </Badge>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="mt-2">
            {brandOptions.length > 1 && onBrandChange ? (
              <div className="mb-2 relative">
                <Select value={String(selectedBrandId ?? '')} onValueChange={onBrandChange}>
                  <SelectTrigger className={cn("h-9 w-full font-semibold border-0 shadow-sm ring-1 ring-inset focus:ring-2 transition-all", 
                      isBrandTheme 
                      ? "bg-white/10 text-white ring-white/20 hover:bg-white/20 focus:ring-white/40" 
                      : (isDark 
                          ? "bg-gray-900/50 text-white ring-gray-700 hover:bg-gray-800" 
                          : "bg-white text-slate-900 ring-slate-200 hover:bg-slate-50")
                  )}>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent className={cn("max-h-[300px]", isBrandTheme ? "bg-gray-900 text-white border border-white/10" : (isDark ? "bg-gray-900 text-white border-gray-800" : "bg-white text-slate-900 border-slate-200"))}>
                    {(() => {
                      // Extract parent brand info from brand profile spec
                      const getParentBrand = (brand) => {
                        const spec = brand.brand_profile_spec || brand.brandProfileSpec || brand.profileSpec;
                        return spec?.brand_info?.parent_brand || null;
                      };

                      // Group brands - first by parent brand (for enterprise brands), then by tenant
                      const grouped = brandOptions.reduce((acc, brand) => {
                        const parentBrand = getParentBrand(brand);
                        
                        // If brand has parent_brand, group under that
                        if (parentBrand?.name) {
                          const groupKey = `🏢 ${parentBrand.name}`;
                          if (!acc[groupKey]) {
                            acc[groupKey] = { 
                              brands: [], 
                              isParentBrand: true, 
                              parentLogo: parentBrand.logo_url,
                              sortOrder: 2 // After My Workspace, before regular tenants
                            };
                          }
                          acc[groupKey].brands.push(brand);
                          return acc;
                        }

                        // Otherwise group by tenant
                        const key = brand.tenantName || "My Workspace";
                        const isOwner = brand.isOwner !== false; 
                        const groupKey = isOwner ? "My Workspace" : key;
                        
                        if (!acc[groupKey]) {
                          acc[groupKey] = { 
                            brands: [], 
                            isParentBrand: false,
                            sortOrder: isOwner ? 1 : 3
                          };
                        }
                        acc[groupKey].brands.push(brand);
                        return acc;
                      }, {});

                      const groups = Object.entries(grouped).sort((a, b) => {
                        // Sort by: My Workspace (1), Parent Brands (2), Regular Tenants (3)
                        const orderA = a[1].sortOrder ?? 3;
                        const orderB = b[1].sortOrder ?? 3;
                        if (orderA !== orderB) return orderA - orderB;
                        return a[0].localeCompare(b[0]);
                      });

                      return groups.map(([groupName, groupData], index) => (
                        <div key={groupName}>
                          {index > 0 && <SelectSeparator className={isBrandTheme || isDark ? "bg-white/10" : "bg-slate-100"} />}
                          <SelectGroup>
                            <SelectLabel className={cn("text-xs pl-2 py-1.5 font-medium opacity-70 flex items-center gap-2", isBrandTheme || isDark ? "text-gray-400" : "text-slate-500")}>
                              {groupData.parentLogo && (
                                <img 
                                  src={groupData.parentLogo} 
                                  alt="" 
                                  className="h-3 w-auto opacity-70"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {groupName}
                            </SelectLabel>
                            {groupData.brands.map((brand) => (
                              <SelectItem key={brand.id} value={String(brand.id)} className="cursor-pointer">
                                {brand.name ?? brand.slug ?? brand.id}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </div>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-sm font-semibold truncate px-1">
                {brandDisplayName}
              </p>
            )}

            <p className="text-xs mt-1 line-clamp-2 px-1 opacity-70">
              {brandDifferentiator}
            </p>
            {voiceStyle && (
              <div className="mt-2 flex items-center gap-1.5 px-1">
                  <Sparkles className="w-3 h-3 opacity-50" />
                  <p className="text-[10px] italic line-clamp-1 opacity-70">
                    Voice: {voiceStyle}
                  </p>
              </div>
            )}

            {environmentOptions.length > 0 && onEnvironmentChange && (
              <div className="mt-3 pt-3 border-t border-dashed border-current/10">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider opacity-50 pl-1">Env</span>
                    <Select value={selectedEnvironment} onValueChange={onEnvironmentChange}>
                      <SelectTrigger className={cn("h-6 text-xs w-auto min-w-[100px] border-0 bg-transparent hover:bg-current/5 focus:ring-0 p-0 px-2 h-auto py-1 rounded-md justify-between gap-1", 
                          isBrandTheme ? "text-white/90" : (isDark ? "text-white/90" : "text-slate-700")
                      )}>
                        <SelectValue placeholder="Env" />
                      </SelectTrigger>
                      <SelectContent className={cn(isBrandTheme ? "bg-gray-900 text-white border border-white/10" : (isDark ? "bg-gray-900 text-white border-gray-800" : "bg-white text-slate-900 border-slate-200"))}>
                        {environmentOptions.map((env) => (
                          <SelectItem key={env.key} value={env.key} className="text-xs">
                            {env.label ?? env.key}
                            {env.is_default ? ' • default' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {!collapsed && (
        <Collapsible
          open={isToolsOpen}
          onOpenChange={setIsToolsOpen}
          className="px-3 py-2 space-y-1"
        >
           <CollapsibleTrigger className="px-2 mb-1 flex items-center gap-2 w-full hover:bg-black/5 dark:hover:bg-white/5 rounded py-1 -ml-2 pl-2 cursor-pointer">
              <ChevronRight className={cn("w-3 h-3 transition-transform duration-200 opacity-40", isToolsOpen && "rotate-90")} />
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-40">Tools</p>
           </CollapsibleTrigger>
           
           <CollapsibleContent className="space-y-1">
            <a
              href={brandOsLink}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 group",
                isBrandTheme 
                  ? "text-white/80 hover:bg-white/10 hover:text-white" 
                  : (isDark 
                      ? "text-gray-400 hover:bg-gray-800 hover:text-white" 
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
              )}
            >
              <div className={cn("p-1 rounded-md transition-colors", isBrandTheme ? "bg-white/10 group-hover:bg-white/20" : "bg-current/5 group-hover:bg-current/10")}>
                <LayoutGrid className="w-4 h-4" />
              </div>
              Brand OS
            </a>
            
            <a
              href={workspacesLink}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 group",
                isBrandTheme 
                  ? "text-white/80 hover:bg-white/10 hover:text-white" 
                  : (isDark 
                      ? "text-gray-400 hover:bg-gray-800 hover:text-white" 
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
              )}
            >
              <div className={cn("p-1 rounded-md transition-colors", isBrandTheme ? "bg-white/10 group-hover:bg-white/20" : "bg-current/5 group-hover:bg-current/10")}>
                 <div className="w-4 h-4 border-2 border-current rounded-sm opacity-60" />
              </div>
              Workspaces
            </a>

            {onOpenResearch && (
              <button
                onClick={(e) => { e.preventDefault(); onOpenResearch(); }}
                className={cn(
                  "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 group text-left",
                  isBrandTheme 
                    ? "text-white/80 hover:bg-white/10 hover:text-white" 
                    : (isDark 
                        ? "text-gray-400 hover:bg-gray-800 hover:text-white" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
                )}
              >
                <div className={cn("p-1 rounded-md transition-colors", isBrandTheme ? "bg-white/10 group-hover:bg-white/20" : "bg-current/5 group-hover:bg-current/10")}>
                  <BarChart3 className="w-4 h-4" />
                </div>
                Market Research
              </button>
            )}

            {onOpenSiteAgent && (
              <button
                onClick={(e) => { e.preventDefault(); onOpenSiteAgent(); }}
                className={cn(
                  "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 group text-left",
                  isBrandTheme 
                    ? "text-white/80 hover:bg-white/10 hover:text-white" 
                    : (isDark 
                        ? "text-gray-400 hover:bg-gray-800 hover:text-white" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
                )}
              >
                <div className={cn("p-1 rounded-md transition-colors", isBrandTheme ? "bg-white/10 group-hover:bg-white/20" : "bg-current/5 group-hover:bg-current/10")}>
                  <Code2 className="w-4 h-4" />
                </div>
                Site Agent
                <span className={cn("ml-auto text-[10px] px-1.5 py-0.5 rounded-full", isBrandTheme ? "bg-cyan-500/20 text-cyan-300" : "bg-cyan-100 text-cyan-700")}>
                  NEW
                </span>
              </button>
            )}
           </CollapsibleContent>
        </Collapsible>
      )}

      <Separator className={cn(borderColor, "opacity-50")} />

      {/* Conversations List */}
      {collapsed ? (
         <ScrollArea className="flex-1 min-w-0 max-w-full mt-2">
            <div className="space-y-2 px-2 pb-2">
               {conversationList.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => onSelectConversation(conv)}
                    className={cn(
                      'w-full p-2 rounded-lg flex items-center justify-center transition-all border border-transparent box-border',
                      activeConversation?.id === conv.id
                        ? conversationActiveClasses
                        : conversationInactiveClasses
                    )}
                  >
                    <MessageSquare className="w-5 h-5" />
                  </button>
               ))}
            </div>
         </ScrollArea>
      ) : (
        <Collapsible
          open={isChatsOpen}
          onOpenChange={setIsChatsOpen}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="px-4 py-2 flex-shrink-0">
             <CollapsibleTrigger className="flex items-center gap-2 w-full hover:bg-black/5 dark:hover:bg-white/5 rounded py-1 -ml-2 pl-2 cursor-pointer">
                <ChevronRight className={cn("w-3 h-3 transition-transform duration-200 opacity-40", isChatsOpen && "rotate-90")} />
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-40">Chats</p>
             </CollapsibleTrigger>
          </div>
          
          <CollapsibleContent className="flex-1 min-h-0 relative data-[state=closed]:hidden">
             <ScrollArea className="h-full absolute inset-0">
                <div className="space-y-1 px-3 pb-3 w-full min-w-0 max-w-full">
                  {conversationList.map((conv) => {
                    // Check if conversation is a branch (has parent_conversation_id)
                    const isBranched = !!(conv.parent_conversation_id || conv.parentConversationId);
                    
                    return (
                    <div
                      key={conv.id}
                      className={cn(
                        'group w-full flex items-center gap-3 rounded-lg border border-transparent transition-all box-border px-3 py-2.5 cursor-pointer',
                        activeConversation?.id === conv.id
                          ? (isBrandTheme ? 'bg-white/10 text-white' : (isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900'))
                          : (isBrandTheme ? 'text-white/70 hover:bg-white/5 hover:text-white' : (isDark ? 'text-gray-400 hover:bg-gray-800/50 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'))
                      )}
                      onClick={() => onSelectConversation(conv)}
                      role="button"
                      tabIndex={0}
                    >
                      {/* Show GitBranch icon for branched conversations, MessageSquare for regular */}
                      {isBranched ? (
                        <GitBranch className="w-4 h-4 flex-shrink-0 opacity-70" />
                      ) : (
                        <MessageSquare className="w-4 h-4 flex-shrink-0 opacity-70" />
                      )}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate leading-tight">
                          {conv.title || 'Untitled conversation'}
                        </p>
                      </div>
                      
                      {(onRenameConversation || onDeleteConversation) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className={cn(
                                "flex-shrink-0 h-6 w-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity",
                                isBrandTheme ? "hover:bg-white/20 text-white" : "hover:bg-black/10 text-current"
                              )}
                              title="Options"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            side="right"
                            sideOffset={8}
                            className={cn("w-44 z-[100]", isBrandTheme || isDark ? "border-white/10 bg-gray-900 text-white" : "border-slate-200 bg-white text-slate-900")}
                          >
                            {onRenameConversation && (
                              <DropdownMenuItem
                                className={cn("cursor-pointer", isBrandTheme || isDark ? "focus:bg-white/10 focus:text-white" : "focus:bg-slate-100 focus:text-slate-900")}
                                onClick={(e) => {
                                  e.preventDefault();
                                  openRenameDialog(conv);
                                }}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                            )}
                            {onDeleteConversation && (
                              <DropdownMenuItem
                                className="cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openDeleteDialog(conv);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    );
                  })}
                </div>
             </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Account Menu */}
      <div className={cn('p-4 border-t', borderColor)}>
        <AccountMenu
          collapsed={collapsed}
          authStatus={authStatus}
          user={sessionUser}
          accountLabel={accountLabel}
          onLogin={onLogin}
          onLogout={onLogout}
          onOpenSettings={() => setShowSettings(true)}
          brandVisualTheme={brandVisualTheme}
        />
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        brandVisualTheme={brandVisualTheme}
        user={sessionUser}
        onUpdateUser={onUpdateUser}
      />
    </div>
    <Dialog
      open={renameOpen}
      onOpenChange={(open) => {
        if (!open) closeRenameDialog();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename conversation</DialogTitle>
          <DialogDescription>Give this conversation a more helpful name.</DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="New conversation name"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={closeRenameDialog} disabled={isRenaming}>
            Cancel
          </Button>
          <Button onClick={handleRenameSubmit} disabled={isRenaming || !renameValue.trim()}>
            {isRenaming ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog
      open={deleteOpen}
      onOpenChange={(open) => {
        if (!open) closeDeleteDialog();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone and will remove the chat history for
            <span className="font-semibold"> {deleteTarget?.title || 'Untitled conversation'}</span>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default Sidebar;
